/**
 * Custom scrollbar web component that provides customizable scrolling functionality
 * with support for different scroll directions and CSS custom properties.
 */
export class XScrollbar extends HTMLElement {
	private container: HTMLDivElement;
	private content: HTMLSlotElement;
	private scrollbarTrack: HTMLDivElement;
	private scrollbarThumb: HTMLDivElement;
	private horizontalScrollbarTrack: HTMLDivElement;
	private horizontalScrollbarThumb: HTMLDivElement;
	private scrollDirection: "left" | "right" | "both" = "both";
	private isScrolling = false;
	private isScrollingHorizontal = false;
	private startY = 0;
	private startX = 0;
	private startScrollTop = 0;
	private startScrollLeft = 0;

	constructor() {
		super();
		this.attachShadow({ mode: "open" });
		this.setupComponent();
	}

	static get observedAttributes() {
		return ["scroll-direction"];
	}

	attributeChangedCallback(name: string, oldValue: string, newValue: string) {
		if (name === "scroll-direction" && oldValue !== newValue) {
			this.scrollDirection = (newValue as "left" | "right" | "both") || "both";
			this.updateScrollbarVisibility();
		}
	}

	connectedCallback() {
		this.setupEventListeners();
		this.updateScrollbarVisibility();

		// Use ResizeObserver to detect content changes
		const resizeObserver = new ResizeObserver(() => {
			this.updateScrollbarVisibility();
		});

		resizeObserver.observe(this.container);

		// Observe the custom element itself for size changes
		if (this.isConnected) {
			resizeObserver.observe(this as unknown as Element);
		}
	}

	private setupComponent() {
		const style = document.createElement("style");
		style.textContent = `
      :host {
        display: block;
        position: relative;
        overflow: hidden;
        --scrollbar-width: var(--x-scrollbar-width, 8px);
        --scrollbar-track-color: var(--x-scrollbar-track-color, rgba(0, 0, 0, 0.1));
        --scrollbar-thumb-color: var(--x-scrollbar-thumb-color, rgba(0, 0, 0, 0.3));
        --scrollbar-thumb-hover-color: var(--x-scrollbar-thumb-hover-color, rgba(0, 0, 0, 0.5));
        --scrollbar-thumb-active-color: var(--x-scrollbar-thumb-active-color, rgba(0, 0, 0, 0.7));
        --scrollbar-border-radius: var(--x-scrollbar-border-radius, 4px);
      }

      .container {
        width: 100%;
        height: 100%;
        overflow: auto;
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE and Edge */
      }

      .container::-webkit-scrollbar {
        display: none; /* Chrome, Safari, Opera */
      }

      .scrollbar-track {
        position: absolute;
        background-color: var(--scrollbar-track-color);
        border-radius: var(--scrollbar-border-radius);
        opacity: 0;
        transition: opacity 0.2s ease;
        z-index: 1;
      }

      .scrollbar-track.vertical {
        top: 0;
        right: 0;
        width: var(--scrollbar-width);
        height: 100%;
      }

      .scrollbar-track.horizontal {
        bottom: 0;
        left: 0;
        width: 100%;
        height: var(--scrollbar-width);
      }

      .scrollbar-thumb {
        position: absolute;
        background-color: var(--scrollbar-thumb-color);
        border-radius: var(--scrollbar-border-radius);
        cursor: pointer;
        transition: background-color 0.2s ease;
      }

      .scrollbar-thumb:hover {
        background-color: var(--scrollbar-thumb-hover-color);
      }

      .scrollbar-thumb:active,
      .scrollbar-thumb.dragging {
        background-color: var(--scrollbar-thumb-active-color);
      }

      :host(:hover) .scrollbar-track {
        opacity: 1;
      }

      .scrollbar-track.visible {
        opacity: 1;
      }

      @media (prefers-reduced-motion: reduce) {
        .scrollbar-track,
        .scrollbar-thumb {
          transition: none;
        }
      }
    `;

		this.container = document.createElement("div");
		this.container.className = "container";

		this.content = document.createElement("slot");
		this.container.appendChild(this.content);

		this.scrollbarTrack = document.createElement("div");
		this.scrollbarTrack.className = "scrollbar-track vertical";

		this.scrollbarThumb = document.createElement("div");
		this.scrollbarThumb.className = "scrollbar-thumb";
		this.scrollbarTrack.appendChild(this.scrollbarThumb);

		this.horizontalScrollbarTrack = document.createElement("div");
		this.horizontalScrollbarTrack.className = "scrollbar-track horizontal";

		this.horizontalScrollbarThumb = document.createElement("div");
		this.horizontalScrollbarThumb.className = "scrollbar-thumb";
		this.horizontalScrollbarTrack.appendChild(this.horizontalScrollbarThumb);

		this.shadowRoot!.appendChild(style);
		this.shadowRoot!.appendChild(this.container);
		this.shadowRoot!.appendChild(this.scrollbarTrack);
		this.shadowRoot!.appendChild(this.horizontalScrollbarTrack);
	}

	private setupEventListeners() {
		// Container scroll events
		this.container.addEventListener("scroll", this.handleScroll.bind(this));

		// Vertical thumb drag events
		this.scrollbarThumb.addEventListener("mousedown", this.handleVerticalThumbMouseDown.bind(this));
		this.scrollbarThumb.addEventListener("touchstart", this.handleVerticalThumbTouchStart.bind(this));

		// Horizontal thumb drag events
		this.horizontalScrollbarThumb.addEventListener("mousedown", this.handleHorizontalThumbMouseDown.bind(this));
		this.horizontalScrollbarThumb.addEventListener("touchstart", this.handleHorizontalThumbTouchStart.bind(this));

		// Track click events
		this.scrollbarTrack.addEventListener("click", this.handleVerticalTrackClick.bind(this));
		this.horizontalScrollbarTrack.addEventListener("click", this.handleHorizontalTrackClick.bind(this));

		// Global mouse/touch events for dragging
		document.addEventListener("mousemove", this.handleMouseMove.bind(this));
		document.addEventListener("mouseup", this.handleMouseUp.bind(this));
		document.addEventListener("touchmove", this.handleTouchMove.bind(this));
		document.addEventListener("touchend", this.handleTouchEnd.bind(this));
	}

	private handleScroll() {
		this.updateVerticalThumbPosition();
		this.updateHorizontalThumbPosition();
	}

	private updateScrollbarVisibility() {
		const hasVerticalOverflow = this.container.scrollHeight > this.container.clientHeight;
		const hasHorizontalOverflow = this.container.scrollWidth > this.container.clientWidth;

		// Show/hide based on scroll direction and overflow
		const shouldShowVertical = hasVerticalOverflow && (this.scrollDirection === "both" || this.scrollDirection === "right");
		const shouldShowHorizontal = hasHorizontalOverflow && (this.scrollDirection === "both" || this.scrollDirection === "left");

		this.scrollbarTrack.style.display = shouldShowVertical ? "block" : "none";
		this.horizontalScrollbarTrack.style.display = shouldShowHorizontal ? "block" : "none";

		if (shouldShowVertical) {
			this.updateVerticalThumbPosition();
		}

		if (shouldShowHorizontal) {
			this.updateHorizontalThumbPosition();
		}
	}

	private updateVerticalThumbPosition() {
		const scrollRatio = this.container.scrollTop / (this.container.scrollHeight - this.container.clientHeight);
		const trackHeight = this.scrollbarTrack.clientHeight;
		const thumbHeight = Math.max((this.container.clientHeight / this.container.scrollHeight) * trackHeight, 20);
		const thumbTop = scrollRatio * (trackHeight - thumbHeight);

		this.scrollbarThumb.style.height = `${thumbHeight}px`;
		this.scrollbarThumb.style.top = `${thumbTop}px`;
	}

	private updateHorizontalThumbPosition() {
		const scrollRatio = this.container.scrollLeft / (this.container.scrollWidth - this.container.clientWidth);
		const trackWidth = this.horizontalScrollbarTrack.clientWidth;
		const thumbWidth = Math.max((this.container.clientWidth / this.container.scrollWidth) * trackWidth, 20);
		const thumbLeft = scrollRatio * (trackWidth - thumbWidth);

		this.horizontalScrollbarThumb.style.width = `${thumbWidth}px`;
		this.horizontalScrollbarThumb.style.left = `${thumbLeft}px`;
	}

	private handleVerticalThumbMouseDown(event: MouseEvent) {
		event.preventDefault();
		this.startVerticalDrag(event.clientY);
	}

	private handleVerticalThumbTouchStart(event: TouchEvent) {
		event.preventDefault();
		const touch = event.touches[0];
		this.startVerticalDrag(touch.clientY);
	}

	private handleHorizontalThumbMouseDown(event: MouseEvent) {
		event.preventDefault();
		this.startHorizontalDrag(event.clientX);
	}

	private handleHorizontalThumbTouchStart(event: TouchEvent) {
		event.preventDefault();
		const touch = event.touches[0];
		this.startHorizontalDrag(touch.clientX);
	}

	private startVerticalDrag(clientY: number) {
		this.isScrolling = true;
		this.startY = clientY;
		this.startScrollTop = this.container.scrollTop;
		this.scrollbarThumb.classList.add("dragging");
		document.body.style.userSelect = "none";
	}

	private startHorizontalDrag(clientX: number) {
		this.isScrollingHorizontal = true;
		this.startX = clientX;
		this.startScrollLeft = this.container.scrollLeft;
		this.horizontalScrollbarThumb.classList.add("dragging");
		document.body.style.userSelect = "none";
	}

	private handleMouseMove(event: MouseEvent) {
		if (this.isScrolling) {
			this.updateVerticalScroll(event.clientY);
		}
		if (this.isScrollingHorizontal) {
			this.updateHorizontalScroll(event.clientX);
		}
	}

	private handleTouchMove(event: TouchEvent) {
		const touch = event.touches[0];
		if (this.isScrolling) {
			this.updateVerticalScroll(touch.clientY);
		}
		if (this.isScrollingHorizontal) {
			this.updateHorizontalScroll(touch.clientX);
		}
	}

	private updateVerticalScroll(clientY: number) {
		const deltaY = clientY - this.startY;
		const trackHeight = this.scrollbarTrack.clientHeight;
		const thumbHeight = this.scrollbarThumb.clientHeight;
		const scrollableHeight = this.container.scrollHeight - this.container.clientHeight;

		const scrollRatio = deltaY / (trackHeight - thumbHeight);
		const newScrollTop = this.startScrollTop + scrollRatio * scrollableHeight;

		this.container.scrollTop = Math.max(0, Math.min(scrollableHeight, newScrollTop));
	}

	private updateHorizontalScroll(clientX: number) {
		const deltaX = clientX - this.startX;
		const trackWidth = this.horizontalScrollbarTrack.clientWidth;
		const thumbWidth = this.horizontalScrollbarThumb.clientWidth;
		const scrollableWidth = this.container.scrollWidth - this.container.clientWidth;

		const scrollRatio = deltaX / (trackWidth - thumbWidth);
		const newScrollLeft = this.startScrollLeft + scrollRatio * scrollableWidth;

		this.container.scrollLeft = Math.max(0, Math.min(scrollableWidth, newScrollLeft));
	}

	private handleMouseUp() {
		this.endDrag();
	}

	private handleTouchEnd() {
		this.endDrag();
	}

	private endDrag() {
		if (this.isScrolling) {
			this.isScrolling = false;
			this.scrollbarThumb.classList.remove("dragging");
		}
		if (this.isScrollingHorizontal) {
			this.isScrollingHorizontal = false;
			this.horizontalScrollbarThumb.classList.remove("dragging");
		}
		document.body.style.userSelect = "";
	}

	private handleVerticalTrackClick(event: MouseEvent) {
		if (event.target === this.scrollbarThumb) return;

		const trackRect = this.scrollbarTrack.getBoundingClientRect();
		const clickPosition = event.clientY - trackRect.top;
		const trackHeight = this.scrollbarTrack.clientHeight;
		const thumbHeight = this.scrollbarThumb.clientHeight;

		const scrollRatio = (clickPosition - thumbHeight / 2) / (trackHeight - thumbHeight);
		const scrollableHeight = this.container.scrollHeight - this.container.clientHeight;

		this.container.scrollTop = Math.max(0, Math.min(scrollableHeight, scrollRatio * scrollableHeight));
	}

	private handleHorizontalTrackClick(event: MouseEvent) {
		if (event.target === this.horizontalScrollbarThumb) return;

		const trackRect = this.horizontalScrollbarTrack.getBoundingClientRect();
		const clickPosition = event.clientX - trackRect.left;
		const trackWidth = this.horizontalScrollbarTrack.clientWidth;
		const thumbWidth = this.horizontalScrollbarThumb.clientWidth;

		const scrollRatio = (clickPosition - thumbWidth / 2) / (trackWidth - thumbWidth);
		const scrollableWidth = this.container.scrollWidth - this.container.clientWidth;

		this.container.scrollLeft = Math.max(0, Math.min(scrollableWidth, scrollRatio * scrollableWidth));
	}

	/**
	 * Programmatically scroll to a specific position
	 */
	public scrollToPosition(options: { top?: number; left?: number; behavior?: "smooth" | "auto" }) {
		this.container.scrollTo(options);
	}

	/**
	 * Get current scroll position
	 */
	public getScrollPosition() {
		return {
			top: this.container.scrollTop,
			left: this.container.scrollLeft,
			maxTop: this.container.scrollHeight - this.container.clientHeight,
			maxLeft: this.container.scrollWidth - this.container.clientWidth,
		};
	}
}

// Register the custom element
if (!customElements.get("x-scrollbar")) {
	customElements.define("x-scrollbar", XScrollbar);
}
